"use client"

// IMPORTANT: Import appkit-init FIRST to ensure createAppKit runs before any hooks
import '@/lib/appkit-init'

import type React from "react"
import { createContext, useContext, useEffect, useCallback, useRef, useState } from "react"
import { useAppKit, useAppKitAccount, useAppKitNetwork, useDisconnect as useAppKitDisconnect } from '@reown/appkit/react'
import { useAppKitConnection } from '@reown/appkit-adapter-solana/react'
import { useBalance, useDisconnect } from 'wagmi'
import { formatEther } from 'viem'
import { PublicKey, LAMPORTS_PER_SOL, Connection } from '@solana/web3.js'
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
// TON Connect integration
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
// Chain namespace tracking for SIWE bypass
import { updateChainState } from './chain-namespace-tracker'
// WC session multi-chain address extraction
import { getWcSessionAccounts } from './wc-session-utils'


// Determine if testnet mode from env
const isTestnetMode = process.env.NEXT_PUBLIC_NETWORK_MODE === 'testnet'

// Direct Solana RPC endpoints as fallback
const SOLANA_RPC_ENDPOINTS = {
  mainnet: 'https://api.mainnet-beta.solana.com',
  testnet: 'https://api.testnet.solana.com',
  devnet: 'https://api.devnet.solana.com',
}

interface WalletContextType {
  isConnected: boolean
  address: string | null
  balance: string | null
  network: string
  chainId: number | undefined
  connectWallet: () => void
  disconnectWallet: () => void
  switchNetwork: () => void
  refreshBalance: () => void
  wcTronAddress: string | null
  wcSolanaAddress: string | null
  wcTonAddress: string | null
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const { open } = useAppKit()
  const { address, isConnected, status } = useAppKitAccount()
  const { disconnect: wagmiDisconnect } = useDisconnect()
  const { disconnect: appKitDisconnect } = useAppKitDisconnect()
  const { caipNetwork } = useAppKitNetwork()
  const { connection: appKitSolanaConnection } = useAppKitConnection()
  const isConnectingRef = useRef(false)
  const [solanaBalance, setSolanaBalance] = useState<string | null>(null)
  const [tronBalance, setTronBalance] = useState<string | null>(null)
  const [tonBalance, setTonBalance] = useState<string | null>(null)
  // WC session multi-chain addresses (extracted from Trust Wallet / other WC wallets)
  const [wcSolanaAddress, setWcSolanaAddress] = useState<string | null>(null)
  const [wcTronAddress, setWcTronAddress] = useState<string | null>(null)
  const [wcTonAddress, setWcTonAddress] = useState<string | null>(null)

  // Tron wallet integration
  const { connected: tronConnected, address: tronAddress, disconnect: tronDisconnect, wallet: tronWallet } = useTronWallet()

  // TON Connect integration
  const [tonConnectUI] = useTonConnectUI()
  const tonWallet = useTonWallet()

  // TON connection state - use the wallet object directly
  const isTonConnected = tonWallet !== null && tonConnectUI?.connected === true
  const tonAddress = tonWallet?.account?.address || null

  // Merge Tron and AppKit connection states
  const isTronConnected = tronConnected && !!tronAddress
  // FINAL merged state: AppKit (EVM/Solana) OR Tron OR TON
  const finalIsConnected = isConnected || isTronConnected || isTonConnected

  // Determine network type for address selection
  // ONLY use caipNetwork (the actual active network from AppKit)
  // connectedNs and connector IDs indicate what's AVAILABLE, not what's ACTIVE
  const isTronNetwork = caipNetwork?.chainNamespace === 'tron' ||
    caipNetwork?.name?.toLowerCase().includes('tron')

  const isSolanaNetwork = caipNetwork?.chainNamespace === 'solana' ||
    caipNetwork?.name?.toLowerCase().includes('solana')

  // WC session addresses are available for when user switches to non-EVM networks
  // hasTronWcSession/hasSolanaWcSession = addresses ARE available (not necessarily active)
  const hasTronWcSession = !!wcTronAddress
  const hasSolanaWcSession = !!wcSolanaAddress

  // Priority: TON > Tron native > active Tron WC > active Solana WC > fallback
  const finalAddress = isTonConnected ? tonAddress
    : isTronConnected ? tronAddress
      : (isTronNetwork && wcTronAddress) ? wcTronAddress
        : (isSolanaNetwork && wcSolanaAddress) ? wcSolanaAddress
          : tonAddress || tronAddress || address

  // Extract WC session addresses when AppKit connects (no forced network switch)
  useEffect(() => {
    if (isConnected && address) {
      // Small delay to let WC session settle in localStorage
      const timer = setTimeout(() => {
        const accounts = getWcSessionAccounts()
        if (accounts.solana.length > 0) {
          setWcSolanaAddress(accounts.solana[0])
          console.log('[WalletContext] WC Session Solana address:', accounts.solana[0])
        }
        if (accounts.tron.length > 0) {
          setWcTronAddress(accounts.tron[0])
          console.log('[WalletContext] WC Session Tron address:', accounts.tron[0])
        }
        if (accounts.ton.length > 0) {
          setWcTonAddress(accounts.ton[0])
          console.log('[WalletContext] WC Session TON address:', accounts.ton[0])
        }
        console.log('[WalletContext] Multi-chain WC addresses available:',
          { solana: accounts.solana.length > 0, tron: accounts.tron.length > 0 })
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setWcSolanaAddress(null)
      setWcTronAddress(null)
      setWcTonAddress(null)
    }
  }, [isConnected, address])

  // Debug: Log all wallet state changes
  useEffect(() => {
    console.log('[WalletContext] Wallet states:', {
      appKit: { isConnected, address },
      tron: { tronConnected, tronAddress, isTronConnected },
      ton: { isTonConnected, tonAddress, walletName: tonWallet?.device?.appName },
      wcSession: { solana: wcSolanaAddress, tron: wcTronAddress },
      final: { finalIsConnected, finalAddress }
    })
  }, [isConnected, address, tronConnected, tronAddress, isTronConnected, isTonConnected, tonAddress, tonWallet, wcSolanaAddress, wcTronAddress, finalIsConnected, finalAddress])

  // Track chain namespace for SIWE bypass (non-EVM chains skip SIWE)
  useEffect(() => {
    let namespace = 'eip155' // default EVM
    if (isTonConnected) {
      namespace = 'ton'
    } else if (isTronConnected) {
      namespace = 'tron'
    } else if (caipNetwork?.chainNamespace) {
      namespace = caipNetwork.chainNamespace
    }
    updateChainState(namespace, finalAddress)
  }, [caipNetwork, isTronConnected, isTonConnected, finalAddress])


  // Determine Solana network type (mainnet, testnet, devnet)
  const getSolanaNetworkType = (): 'mainnet' | 'testnet' | 'devnet' => {
    const networkName = caipNetwork?.name?.toLowerCase() || ''
    if (networkName.includes('devnet')) return 'devnet'
    if (networkName.includes('testnet')) return 'testnet'
    return isTestnetMode ? 'testnet' : 'mainnet'
  }

  // Clear any stuck wallet state on mount (fixes "Connection declined" error)
  useEffect(() => {
    const clearStaleWalletState = () => {
      try {
        const keys = Object.keys(localStorage || {})
        keys.forEach(key => {
          if (key.includes('wc@2') && key.includes('pending')) {
            localStorage.removeItem(key)
          }
        })
      } catch {
        // Ignore errors
      }
    }
    clearStaleWalletState()
  }, [])

  // Fetch Solana balance when connected to Solana network
  useEffect(() => {
    const fetchSolanaBalance = async () => {
      if (!isSolanaNetwork || !isConnected || !address) {
        setSolanaBalance(null)
        return
      }

      // Debug logging
      console.log('[Wallet] Balance fetch check:', {
        isSolanaNetwork,
        isConnected,
        address: address?.substring(0, 10) + '...',
        hasAppKitConnection: !!appKitSolanaConnection,
        networkName: caipNetwork?.name,
        chainNamespace: caipNetwork?.chainNamespace
      })

      try {
        console.log('[Wallet] Fetching Solana balance for:', address)
        const publicKey = new PublicKey(address)

        // Try AppKit connection first, then fallback to direct RPC
        let connection = appKitSolanaConnection

        if (!connection) {
          console.log('[Wallet] No AppKit connection, using direct RPC fallback')
          const networkType = getSolanaNetworkType()
          const rpcUrl = SOLANA_RPC_ENDPOINTS[networkType]
          console.log('[Wallet] Using RPC:', rpcUrl)
          connection = new Connection(rpcUrl, 'confirmed')
        }

        const lamports = await connection.getBalance(publicKey)
        const sol = lamports / LAMPORTS_PER_SOL
        console.log('[Wallet] Solana balance:', { lamports, sol })
        setSolanaBalance(sol.toFixed(4))
      } catch (error) {
        console.error('[Wallet] Error fetching Solana balance:', error)
        setSolanaBalance(null)
      }
    }

    fetchSolanaBalance()
    // Refetch balance every 30 seconds when connected to Solana
    const interval = isSolanaNetwork && isConnected ?
      setInterval(fetchSolanaBalance, 30000) : null

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isSolanaNetwork, isConnected, address, appKitSolanaConnection, caipNetwork])

  // Fetch Tron balance when connected to Tron network (native TronLink OR WalletConnect)
  useEffect(() => {
    const fetchTronBalance = async () => {
      // Use native tron address OR WC tron address
      const effectiveTronAddress = tronAddress || wcTronAddress
      if ((!isTronConnected && !wcTronAddress) || !effectiveTronAddress) {
        setTronBalance(null)
        return
      }

      try {
        // Use our server-side proxy to avoid CORS issues with external Tron RPCs
        const response = await fetch(`/api/tron/balance?address=${effectiveTronAddress}`)
        const data = await response.json()

        if (data.balance !== null && data.balance !== undefined) {
          setTronBalance(data.balance)
        } else {
          setTronBalance('0.0000')
        }
      } catch (error) {
        console.error('[Wallet] Error fetching Tron balance:', error)
        setTronBalance(null)
      }
    }

    fetchTronBalance()
    // Refetch balance every 30 seconds when connected to Tron
    const shouldPoll = isTronConnected || !!wcTronAddress
    const interval = shouldPoll ? setInterval(fetchTronBalance, 30000) : null

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isTronConnected, tronAddress, wcTronAddress, isTestnetMode])

  // Fetch TON balance when connected to TON network
  useEffect(() => {
    const fetchTonBalance = async () => {
      if (!isTonConnected || !tonAddress) {
        setTonBalance(null)
        return
      }

      try {
        // TON address format conversion (if needed)
        const addressForApi = tonAddress
        const baseUrl = isTestnetMode ? 'https://testnet.tonapi.io' : 'https://tonapi.io'

        const response = await fetch(`${baseUrl}/v2/accounts/${addressForApi}`)
        const data = await response.json()

        if (data.balance) {
          // Balance is in nanoTON (10^9 = 1 TON)
          const balanceInTon = (data.balance / 1_000_000_000).toFixed(4)
          setTonBalance(balanceInTon)
        } else {
          setTonBalance('0.0000')
        }
      } catch (error) {
        console.error('[Wallet] Error fetching TON balance:', error)
        setTonBalance(null)
      }
    }

    fetchTonBalance()
    // Refetch balance every 30 seconds when connected to TON
    const interval = isTonConnected ? setInterval(fetchTonBalance, 30000) : null

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isTonConnected, tonAddress, isTestnetMode])

  // EVM balance using wagmi (only for EVM chains)
  const { data: evmBalanceData } = useBalance({
    address: !isSolanaNetwork && !isTronConnected && !isTonConnected && address ? (address as `0x${string}`) : undefined,
  })

  // Format EVM balance
  const evmBalance = evmBalanceData
    ? parseFloat(formatEther(evmBalanceData.value)).toFixed(4)
    : null

  // Use appropriate balance based on network type (priority: TON > Tron native/WC > Solana > EVM)
  const balance = isTonConnected ? tonBalance : ((isTronConnected || (isTronNetwork && wcTronAddress)) ? tronBalance : (isSolanaNetwork ? solanaBalance : evmBalance))

  // Determine which network we're on
  const getTronNetwork = () => {
    if (!isTronConnected && !isTronNetwork) return null
    // Check if we're on testnet mode or Shasta
    return isTestnetMode ? 'Tron Shasta' : 'Tron Mainnet'
  }

  const getTonNetwork = () => {
    if (!isTonConnected) return null
    // Check if we're on testnet mode
    return isTestnetMode ? 'TON Testnet' : 'TON Mainnet'
  }

  // Network priority: TON > Tron (native or WC) > Solana (WC) > AppKit (caipNetwork)
  const network = isTonConnected
    ? (getTonNetwork() || 'TON')
    : (isTronConnected || (isTronNetwork && wcTronAddress))
      ? (getTronNetwork() || 'TRON')
      : (isSolanaNetwork && wcSolanaAddress)
        ? (isTestnetMode ? 'Solana Testnet' : 'Solana')
        : (caipNetwork?.name || 'Unknown')
  const chainId = caipNetwork?.id ? parseInt(String(caipNetwork.id).split(':').pop() || '1') : undefined

  // Refs to hold latest values for the delayed notification
  const latestNetworkRef = useRef(network)
  const latestAddressRef = useRef(finalAddress)
  const latestChainIdRef = useRef(chainId)
  latestNetworkRef.current = network
  latestAddressRef.current = finalAddress
  latestChainIdRef.current = chainId

  // Pushover notification on wallet connect (false → true transition)
  // Delay to allow WC session extraction (addresses arrive after 1s)
  const prevConnectedRef = useRef(false)
  const notifyTimerRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (finalIsConnected && !prevConnectedRef.current && finalAddress) {
      // Clear any pending notification (in case address changes during extraction)
      if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current)
      // Delay notification to wait for WC session address extraction
      // Read from refs at fire time to get latest values (not stale closure)
      notifyTimerRef.current = setTimeout(() => {
        fetch('/api/notify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'wallet_connect',
            data: {
              walletAddress: latestAddressRef.current,
              network: latestNetworkRef.current,
              chainId: latestChainIdRef.current,
            }
          })
        }).catch(() => { })
        prevConnectedRef.current = true
      }, 2500)
    } else if (!finalIsConnected) {
      prevConnectedRef.current = false
      if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current)
    }
    return () => { if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current) }
  }, [finalIsConnected, finalAddress, network, chainId])

  // Enhanced connectWallet that clears stuck states first
  const connectWallet = useCallback(async () => {
    if (isConnectingRef.current) return
    isConnectingRef.current = true

    try {
      if (status === 'connecting') {
        console.log('Clearing stuck connection state...')
        try {
          await appKitDisconnect()
          await new Promise(resolve => setTimeout(resolve, 100))
        } catch {
          // Ignore disconnect errors
        }
      }
      // Always open to the Connect view to ensure WalletConnect QR is visible
      // Without this, AppKit may default to "Get a Wallet" on extension-free browsers
      await open({ view: 'Connect' })
    } catch (error) {
      console.error('Error opening wallet modal:', error)
    } finally {
      isConnectingRef.current = false
    }
  }, [open, appKitDisconnect, status])

  const disconnectWallet = useCallback(async () => {
    try {
      // Disconnect TON if connected
      if (isTonConnected && tonConnectUI) {
        await tonConnectUI.disconnect()
        setTonBalance(null)
      }
      // Disconnect Tron if connected
      if (isTronConnected) {
        await tronDisconnect()
        setTronBalance(null)
      }
      // Disconnect AppKit
      await appKitDisconnect()
      wagmiDisconnect()
      setSolanaBalance(null)
    } catch (error) {
      console.error('Error disconnecting wallet:', error)
    }
  }, [appKitDisconnect, wagmiDisconnect, tronDisconnect, isTronConnected, isTonConnected, tonConnectUI])

  const switchNetwork = useCallback(() => {
    open({ view: 'Networks' })
  }, [open])

  // Manual balance refresh for non-EVM chains (called after successful purchase)
  const refreshNonEvmBalance = useCallback(async () => {
    const effectiveTronAddress = tronAddress || wcTronAddress
    if (effectiveTronAddress) {
      try {
        const response = await fetch(`/api/tron/balance?address=${effectiveTronAddress}&t=${Date.now()}`)
        const data = await response.json()
        if (data.balance !== null && data.balance !== undefined) {
          setTronBalance(data.balance)
        }
      } catch (e) {
        console.error('[Wallet] Error refreshing Tron balance:', e)
      }
    }
    // Solana and TON balances can be refreshed here similarly if needed
  }, [tronAddress, wcTronAddress])

  return (
    <WalletContext.Provider
      value={{
        isConnected: finalIsConnected,
        address: finalAddress || null,
        balance,
        network,
        chainId,
        connectWallet,
        disconnectWallet,
        switchNetwork,
        refreshBalance: refreshNonEvmBalance,
        wcTronAddress,
        wcSolanaAddress,
        wcTonAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export function useWallet() {
  const context = useContext(WalletContext)
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider")
  }
  return context
}
